import type React from 'react';
import { useState } from 'react';

import { addNotification } from 'notifications';

import {
  personalDataStatusLabel,
  useCreatePersonalDataRequest,
  useMyPersonalDataRequest,
  VK_COMMUNITY_URL,
} from 'hooks/usePersonalDataChange';
import s4 from './s4Screen.module.scss';
import classes from './PersonalDataChangeRequestSection.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

const ACTIVE_STATUSES = new Set([
  'pending_review',
  'awaiting_user_decision',
  'awaiting_otp',
  'expired',
]);

const REASON_MIN = 10;
const REASON_MAX = 2000;

export const PersonalDataChangeRequestSection: React.FC = () => {
  const [reason, setReason] = useState('');
  const { data: mine, isLoading } = useMyPersonalDataRequest();
  const createMutation = useCreatePersonalDataRequest();

  const hasActive = mine && ACTIVE_STATUSES.has(mine.status);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void createMutation
      .mutateAsync(reason.trim())
      .then((created) => {
        const requestId = (created as { id?: number })?.id;
        addNotification(
          requestId
            ? `Заявка №${requestId} отправлена в техподдержку`
            : 'Заявка отправлена в техподдержку',
          'success',
        );
        setReason('');
      })
      .catch((error: unknown) => {
        const detail =
          (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'Не удалось отправить заявку';
        addNotification(typeof detail === 'string' ? detail : 'Не удалось отправить заявку', 'error');
      });
  };

  return (
    <section className={s4.card}>
      <h2 className={s4.cardTitle}>Изменение персональных данных</h2>
      <p className={s4.body}>
        ФИО, пол, дату рождения, телефон и почту нельзя изменить самостоятельно. Опиши причину - мы
        отправим письмо с номером заявки.
      </p>

      {isLoading ? (
        <BrandLoader size='sm' label='Загрузка...' />
      ) : mine ? (
        <div className={classes.statusCard}>
          <span className={s4.label}>Заявка №{mine.id}</span>
          <strong className={s4.body}>{personalDataStatusLabel(mine.status)}</strong>
          {mine.status === 'pending_review' && !mine.contact_email_sent_at && (
            <p className={s4.muted}>
              Сохрани номер <strong>№{mine.id}</strong> - он понадобится при обращении в поддержку.
              Модератор свяжется по почте аккаунта.
            </p>
          )}
          {mine.contact_email_sent_at && (
            <p className={s4.muted}>
              Проверь почту: в письме указан номер заявки <strong>№{mine.id}</strong>. Напиши в{' '}
              <a href={VK_COMMUNITY_URL} target='_blank' rel='noopener noreferrer'>
                сообщество ВКонтакте
              </a>{' '}
              и передай этот номер модератору.
            </p>
          )}
          {mine.comment && <p className={s4.body}>{mine.comment}</p>}
          {mine.status === 'awaiting_otp' && (
            <p className={s4.muted}>
              Проверь почту и введи код на странице из письма или открой ссылку подтверждения еще
              раз.
            </p>
          )}
        </div>
      ) : null}

      {!hasActive && (
        <form className={classes.form} onSubmit={handleSubmit}>
          <label className={s4.field}>
            <span className={s4.label}>Причина изменения</span>
            <textarea
              className={s4.area}
              rows={5}
              value={reason}
              placeholder='Опиши, какие данные нужно исправить и почему'
              onChange={(event) => setReason(event.target.value.slice(0, REASON_MAX))}
            />
          </label>
          <p className={s4.muted}>
            {reason.length} из {REASON_MAX}. От {REASON_MIN} символов.
          </p>
          <button
            type='submit'
            className={s4.primary}
            disabled={createMutation.isPending || reason.trim().length < REASON_MIN}
          >
            {createMutation.isPending ? 'Отправка...' : 'Отправить заявку'}
          </button>
        </form>
      )}
    </section>
  );
};
